<template>
  <div class="inquiry-submit-container">

    <div class="form-container">

      <h2 class="page-title" style="margin-bottom: 20px;text-align: center;">{{ isEditMode ? '编辑询价单' : '发布询价单' }}</h2>

      <el-form 
        ref="inquiryForm" 
        :model="formData" 
        :rules="formRules" 
        label-width="120px"
        v-loading="loading"
      >
        <!-- 产品名称 -->
        <el-form-item label="产品名称" prop="productName">
          <el-input 
            v-model="formData.productName" 
            placeholder="请输入产品名称"
            maxlength="100"
            show-word-limit
          />
        </el-form-item>

        <!-- 产品分类 -->
        <el-form-item label="产品分类" prop="categoryId">
          <el-select 
            v-model="formData.categoryId" 
            placeholder="请选择产品分类"
            style="width: 100%"
            @change="onCategoryChange"
          >
            <el-option
              v-for="category in productCategories"
              :key="category.category_id"
              :label="category.category_name"
              :value="category.category_id"
            />
          </el-select>
        </el-form-item>

        <!-- 产品图片 -->
        <el-form-item label="产品图片" prop="productImages">
          <div class="upload-container">
            <el-upload
              ref="upload"
              :action="uploadUrl"
              :headers="uploadHeaders"
              :data="uploadData"
              :file-list="fileList"
              :on-success="handleUploadSuccess"
              :on-error="handleUploadError"
              :on-remove="handleRemove"
              :before-upload="beforeUpload"
              list-type="picture-card"
              :limit="8"
              accept="image/*"
            >
              <i class="el-icon-plus"></i>
            </el-upload>
            <div class="upload-tip">最多可上传8张图片，支持jpg、png格式，单张图片不超过2MB</div>
          </div>
        </el-form-item>

        <!-- 详细描述 -->
        <el-form-item label="详细描述" prop="description">
          <el-input
            type="textarea"
            v-model="formData.description"
            placeholder="请详细描述产品需求，包括规格、用途、质量要求等"
            :rows="6"
            maxlength="1000"
            show-word-limit
          />
        </el-form-item>

        <!-- 预计需求数量 -->
        <el-form-item label="预计需求数量" prop="quantity">
          <el-input-number
            v-model="formData.quantity"
            :min="1"
            :max="999999"
            placeholder="请输入预计需求数量"
            style="width: 200px"
          />
        </el-form-item>

        <!-- 需求有效期至 -->
        <el-form-item label="需求有效期至" prop="validDate">
          <el-date-picker
            v-model="formData.validDate"
            type="date"
            placeholder="请选择有效期"
            format="yyyy-MM-dd"
            value-format="yyyy-MM-dd"
            :picker-options="validDateOptions"
            style="width: 200px"
          />
        </el-form-item>

        <!-- 期望到货日期 -->
        <el-form-item label="期望到货日期" prop="deliveryDateRange">
          <el-date-picker
            v-model="formData.deliveryDateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            format="yyyy-MM-dd"
            value-format="yyyy-MM-dd"
            :picker-options="deliveryDateOptions"
            style="width: 300px"
          />
        </el-form-item>

        <!-- 联系电话 -->
        <el-form-item label="联系电话" prop="contactPhone">
          <el-input
            v-model="formData.contactPhone"
            placeholder="请输入联系电话"
            maxlength="11"
            style="width: 200px"
          />
        </el-form-item>

        <!-- 提交按钮 -->
        <el-form-item>
          <div class="submit-buttons">
            <el-button @click="goBack">取消</el-button>
            <el-button type="primary" @click="submitForm" :loading="submitting">
              {{ isEditMode ? '更新询价单' : '发布询价单' }}
            </el-button>
          </div>
        </el-form-item>
      </el-form>
    </div>
  </div>
</template>

<script>
import { tree } from '@/api/goods/goodscategory'
import { getInquiryDetail, submitInquiry, updateInquiry } from '@/api/inquiry'
import Config from '@/utils/config'

export default {
  name: 'InquirySubmit',
  data() {
    return {
      // 编辑模式相关
      isEditMode: false,
      inquiryId: '',
      loading: false,
      submitting: false,
      
      // 表单数据
      formData: {
        productName: '',
        categoryId: '',
        productImages: [],
        description: '',
        quantity: '',
        validDate: '',
        deliveryDateRange: [],
        contactPhone: ''
      },
      
      // 表单验证规则
      formRules: {
        productName: [
          { required: true, message: '请输入产品名称', trigger: 'blur' },
          { min: 2, max: 100, message: '产品名称长度在 2 到 100 个字符', trigger: 'blur' }
        ],
        categoryId: [
          { required: true, message: '请选择产品分类', trigger: 'change' }
        ],
        productImages: [
          { required: true, message: '请上传产品图片', trigger: 'change' }
        ],
        description: [
          { required: true, message: '请输入详细描述', trigger: 'blur' },
          { min: 2, max: 1000, message: '描述长度在 2 到 1000 个字符', trigger: 'blur' }
        ],
        quantity: [
          { required: true, message: '请输入预计需求数量', trigger: 'blur' },
          { type: 'number', min: 1, message: '数量必须大于0', trigger: 'blur' }
        ],
        validDate: [
          { required: true, message: '请选择需求有效期', trigger: 'change' }
        ],
        deliveryDateRange: [
          { required: true, message: '请选择期望到货日期', trigger: 'change' }
        ],
        contactPhone: [
          { required: true, message: '请输入联系电话', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号码', trigger: 'blur' }
        ]
      },
      
      // 产品分类数据
      productCategories: [],
      
      // 图片上传相关
      fileList: [],
      uploadUrl: Config.baseUrl + '/api/upload/inquiryimg',
      uploadHeaders: {},
      uploadData: {
        token : this.$store.getters.token,
        path: 'inquiryimg'
      },
      
      // 日期选择器配置
      validDateOptions: {
        disabledDate(time) {
          return time.getTime() < Date.now() - 8.64e7; // 不能选择今天之前的日期
        }
      },
      deliveryDateOptions: {
        disabledDate(time) {
          return time.getTime() < Date.now() - 8.64e7; // 不能选择今天之前的日期
        }
      }
    }
  },
  
  created() {
    this.checkEditMode();
    this.getCategoryList();
    
    // 如果是编辑模式，在分类列表加载完成后获取详情
    if (this.isEditMode) {
      // 延迟执行，确保分类列表先加载
      setTimeout(() => {
        this.getInquiryDetailData();
      }, 800);
    }
  },
  
  methods: {
    // 检查是否为编辑模式
    checkEditMode() {
      const { inquiry_id, mode } = this.$route.query;
      if (inquiry_id && mode === 'edit') {
        this.isEditMode = true;
        this.inquiryId = inquiry_id;
        console.log('编辑模式，询价单ID:', this.inquiryId);
      }
    },
    
    // 获取产品分类列表
    async getCategoryList() {
      console.log('开始获取产品分类列表...');
      try {
        const response = await tree();
        console.log('分类接口响应:', response);
        
        if (response.code === 0 && response.data) {
          // 只保存一级分类
          this.productCategories = response.data.filter(item => item.level === 1);
          console.log('一级分类数据:', this.productCategories);
        } else {
          console.error('分类接口返回错误:', response);
          this.$message.error(response.message || '获取分类列表失败');
        }
      } catch (error) {
        console.error('分类接口请求失败:', error);
        this.$message.error('获取分类列表失败');
      }
    },
    
    // 分类选择变化
    onCategoryChange(categoryId) {
      const category = this.productCategories.find(item => item.category_id === categoryId);
      if (category) {
        this.formData.productCategory = category.category_name;
      }
    },
    
    // 图片上传前验证
    beforeUpload(file) {
      const isImage = file.type.indexOf('image/') === 0;
      const isLt2M = file.size / 1024 / 1024 < 2;
      
      if (!isImage) {
        this.$message.error('只能上传图片文件!');
        return false;
      }
      if (!isLt2M) {
        this.$message.error('上传图片大小不能超过 2MB!');
        return false;
      }
      return true;
    },
    
    // 图片上传成功
    handleUploadSuccess(response, file, fileList) {
      console.log('图片上传响应:', response);
      
      // 根据实际API响应格式判断成功状态
      if (response.code === 10069 || response.message === '上传成功') {
        // 使用实际返回的图片路径字段 pic_path
        const imageUrl = response.data.pic_path || response.data.url;
        this.formData.productImages.push(imageUrl);
        this.fileList = fileList;
        this.$message.success('图片上传成功');
        
        console.log('当前产品图片列表:', this.formData.productImages);
        
        // 触发表单验证
        this.$refs.inquiryForm.validateField('productImages');
      } else {
        this.$message.error(response.message || '图片上传失败');
        // 移除失败的文件
        this.fileList = fileList.filter(item => item.uid !== file.uid);
      }
    },
    
    // 图片上传失败
    handleUploadError(error, file, fileList) {
      this.$message.error('图片上传失败，请重试');
      this.fileList = fileList.filter(item => item.uid !== file.uid);
    },
    
    // 移除图片
    handleRemove(file, fileList) {
      // 从productImages中移除对应的图片
      const index = this.fileList.findIndex(item => item.uid === file.uid);
      if (index > -1) {
        this.formData.productImages.splice(index, 1);
      }
      this.fileList = fileList;
      
      // 触发表单验证
      this.$refs.inquiryForm.validateField('productImages');
    },
    
    // 获取询价单详情（编辑模式）
    async getInquiryDetailData() {
      if (!this.inquiryId) {
        console.error('询价单ID为空');
        return;
      }
      
      console.log('开始获取询价单详情，ID:', this.inquiryId);
      this.loading = true;
      
      try {
        // 调用API函数，避免与方法名冲突
        const response = await getInquiryDetail({ id: this.inquiryId });
        console.log('询价单详情响应:', response);
        
        if (response.code === 0 && response.data) {
          console.log('开始填充表单数据:', response.data);
          this.fillFormData(response.data);
        } else {
          console.error('获取详情失败，响应:', response);
          this.$message.error(response.message || '获取详情失败');
        }
      } catch (error) {
        console.error('获取询价单详情失败:', error);
        this.$message.error('获取详情失败');
      } finally {
        this.loading = false;
      }
    },
    
    // 填充表单数据
    fillFormData(data) {
      console.log('开始填充表单数据:', data);
      
      // 填充基本信息
      this.formData.productName = data.product_name || '';
      this.formData.categoryId = data.category_id || '';
      this.formData.description = data.description || '';
      this.formData.quantity = data.quantity || 1;
      this.formData.validDate = data.validity_date || '';
      this.formData.contactPhone = data.contact_phone || '';
      
      // 处理产品图片数组
      if (data.product_images) {
        if (Array.isArray(data.product_images)) {
          // 如果是数组，直接使用
          this.formData.productImages = [...data.product_images];
          console.log('图片数组处理完成:', this.formData.productImages);
        } else if (typeof data.product_images === 'string') {
          // 如果是字符串，按逗号分割
          this.formData.productImages = data.product_images.split(',').filter(img => img.trim());
          console.log('图片字符串转数组完成:', this.formData.productImages);
        } else {
          this.formData.productImages = [];
        }
      } else {
        this.formData.productImages = [];
      }
      
      // 处理期望到货日期（格式：开始日期,结束日期）
      if (data.expected_arrival_date) {
        const dates = data.expected_arrival_date.split(',');
        if (dates.length >= 2) {
          this.formData.deliveryDateRange = [dates[0].trim(), dates[1].trim()];
        } else if (dates.length === 1) {
          // 如果只有一个日期，设置为开始日期，结束日期为空
          this.formData.deliveryDateRange = [dates[0].trim(), ''];
        }
      }
      
      // 处理图片文件列表用于显示
      if (this.formData.productImages && this.formData.productImages.length > 0) {
        this.fileList = this.formData.productImages.map((url, index) => ({
          uid: index,
          name: `image-${index}`,
          status: 'done',
          url: this.$img(url)
        }));
        console.log('文件列表处理完成:', this.fileList);
      }
      
      // 设置产品分类（如果分类列表已加载）
      if (data.category_id && this.productCategories.length > 0) {
        const category = this.productCategories.find(item => item.category_id == data.category_id);
        if (category) {
          this.formData.categoryId = category.category_id;
        }
      }
      
      console.log('表单数据填充完成:', this.formData);
    },
    
    // 提交表单
    submitForm() {
      this.$refs.inquiryForm.validate(async (valid) => {
        if (!valid) return;
        
        // 验证图片
        if (this.formData.productImages.length === 0) {
          this.$message.error('请上传产品图片');
          return;
        }
        
        // 验证日期范围
        if (this.formData.deliveryDateRange.length !== 2) {
          this.$message.error('请选择完整的期望到货日期范围');
          return;
        }
        
        // 验证日期逻辑
        const validDate = new Date(this.formData.validDate);
        const deliveryEndDate = new Date(this.formData.deliveryDateRange[1]);
        if (validDate >= deliveryEndDate) {
          this.$message.error('需求有效期必须小于期望到货结束日期');
          return;
        }
        
        this.submitting = true;
        
        try {
          // 构建提交数据，参考App端的数据格式
          const submitData = {
            product_name: this.formData.productName,
            // 将图片数组转换为逗号分隔的字符串
            product_images: Array.isArray(this.formData.productImages) 
              ? this.formData.productImages.join(',') 
              : this.formData.productImages || '',
            category_id: this.formData.categoryId,
            description: this.formData.description,
            quantity: parseInt(this.formData.quantity),
            validity_date: this.formData.validDate,
            expected_arrival_date: `${this.formData.deliveryDateRange[0]},${this.formData.deliveryDateRange[1]}`,
            contact_phone: this.formData.contactPhone
          };
          
          console.log('提交数据格式:', submitData);
          console.log('图片数据类型:', typeof submitData.product_images, '值:', submitData.product_images);
          
          // 如果是编辑模式，添加ID
          if (this.isEditMode) {
            submitData.id = this.inquiryId;
          }
          
          // 调用API
          let response;
          if (this.isEditMode) {
            response = await updateInquiry(submitData);
          } else {
            response = await submitInquiry(submitData);
          }
          
          if (response.code === 0) {
            this.$message.success(this.isEditMode ? '更新成功' : '发布成功');
            setTimeout(() => {
              this.goBack();
            }, 1500);
          } else {
            this.$message.error(response.message || (this.isEditMode ? '更新失败' : '发布失败'));
          }
        } catch (error) {
          this.$message.error('网络错误，请重试');
        } finally {
          this.submitting = false;
        }
      });
    },
    
    // 返回上一页
    goBack() {
      this.$router.go(-1);
    }
  }
}
</script>

<style lang="scss" scoped>
.inquiry-submit-container {
  padding: 20px;
  background-color: #f5f5f5;
  min-height: calc(100vh - 60px);
}

.page-header {
  background: #fff;
  padding: 20px;
  margin-bottom: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  .page-title {
    margin: 0;
    font-size: 24px;
    font-weight: 600;
    color: #333;
  }
}

.form-container {
  background: #fff;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  .el-form {
    max-width: 800px;
  }
  
  .el-form-item {
    margin-bottom: 24px;
  }
}

.upload-container {
  .upload-tip {
    margin-top: 8px;
    font-size: 12px;
    color: #999;
    line-height: 1.4;
  }
}

.submit-buttons {
  display: flex;
  gap: 16px;
  padding-top: 20px;
  
  .el-button {
    min-width: 120px;
  }
}

// Element UI 样式覆盖
:deep(.el-upload--picture-card) {
  width: 100px;
  height: 100px;
  line-height: 100px;
}

:deep(.el-upload-list--picture-card .el-upload-list__item) {
  width: 100px;
  height: 100px;
}

:deep(.el-form-item__label) {
  font-weight: 500;
  color: #333;
}

:deep(.el-input__inner),
:deep(.el-textarea__inner) {
  border-radius: 6px;
}

:deep(.el-button) {
  border-radius: 6px;
}

// 响应式设计
@media (max-width: 768px) {
  .inquiry-submit-container {
    padding: 10px;
  }
  
  .page-header,
  .form-container {
    padding: 15px;
  }
  
  .el-form {
    max-width: 100% !important;
  }
  
  .submit-buttons {
    flex-direction: column;
    
    .el-button {
      width: 100%;
    }
  }
}
</style>